import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject } from 'rxjs';
import { SystemApp } from '../../constans/SystemConfigurations';
import { CouponService } from '../../services/coupon.service';

export interface ChatRoomSummary {
  id: number;
  name: string;
  last_activity: string;
  lastMessage: string;
  unreadCount: number;
}

export interface ChatReaction {
  emoji: string;
  count: number;
  reacted: boolean;
}

export interface ChatMsg {
  id: string;
  role: 'bot' | 'user';
  text: string;
  options?: ChatOpt[];
  reactions?: ChatReaction[];
  /** Rich content rendered under the bubble (e.g. the subscription plan cards). */
  widget?: ChatWidget;
}

/** Rich content under a bot bubble, data loaded from the API:
 * 'ad_plans' = per-ad plan cards (AdsRoute), 'subscription_plans' = Go / Plus / Pro cards,
 * 'contact' = every contact channel (company-api/profile/info/). */
export type ChatWidget = 'ad_plans' | 'subscription_plans' | 'contact';

/**
 * A card picked in the plan cards.
 * - kind 'ad': `id` = AdsRoute (per-ad plan).
 * - kind 'subscription': `id` = SubscriptionPlan row of the billing period shown when it was picked.
 * - kind 'coupon' (SCRUM-313): `id` = coupon redemption the user picked; the
 *   plan picker at the end of the publish form pre-highlights it and publishes.
 */
export interface ChatPickedPlan {
  kind: 'ad' | 'subscription' | 'coupon';
  id: number;
  name: string;
  billingPeriod?: 'MONTHLY' | 'YEARLY';
}

export interface ChatOpt {
  label: string;
  value: string;
  disabled?: boolean;
}

export type ChatMode = 'menu' | 'publish_category' | 'navigate' | 'qa' | 'live_chat' | 'room_picker';

@Injectable({ providedIn: 'root' })
export class ChatbotService {

  messages$ = new BehaviorSubject<ChatMsg[]>([]);
  isTyping$ = new BehaviorSubject<boolean>(false);
  mode$     = new BehaviorSubject<ChatMode>('menu');
  roomsForPicker$ = new BehaviorSubject<ChatRoomSummary[]>([]);
  // The room the picker chose, if any — read by ChatbotComponent's template
  // and passed to <app-chat-room [targetRoomId]>, which does the ONE actual
  // reconnect itself on mount. Deliberately NOT reconnected here too — see
  // ChatRoomComponent's targetRoomId doc comment for why a second reconnect
  // from this service was racing the component's own and losing messages.
  selectedRoomId$ = new BehaviorSubject<number | null>(null);

  constructor(
    private router: Router,
    private http: HttpClient,
    private couponService: CouponService,
  ) {}

  private isLoggedIn(): boolean {
    return !!localStorage.getItem('is_logged_in');
  }

  init(): void {
    this.messages$.next([]);
    this.isTyping$.next(false);
    this.mode$.next('menu');
    this.botSay(
      'שלום! אני Adaline, הבוט החכם של Sale4All 👋\nאיך אפשר לעזור לך היום?',
      [
        { label: '📢 פרסם מודעה',      value: 'publish' },
        { label: '🔍 נווט באתר',        value: 'navigate' },
        { label: '❓ שאל שאלה',         value: 'qa' },
        { label: '💬 שוחח עם נציג חי', value: 'live_chat', disabled: !this.isLoggedIn() },
      ]
    );
  }

  handleMenuChoice(value: string): void {
    const labels: Record<string, string> = {
      publish:   '📢 פרסם מודעה',
      navigate:  '🔍 נווט באתר',
      qa:        '❓ שאל שאלה',
      live_chat: '💬 שוחח עם נציג חי',
    };
    this.userSay(labels[value] ?? value);
    if (value === 'publish')        this.startPublish();
    else if (value === 'navigate')  this.startNavigate();
    else if (value === 'live_chat') this.requireLoginThenChat();
    else                            this.startQA();
  }

  private startPublish(prompt = 'באיזה קטגוריה תרצה לפרסם?'): void {
    this.mode$.next('publish_category');
    this.botSay(prompt, [
      { label: '🚗 רכב',              value: 'car' },
      { label: '🏠 נדל"ן',             value: 'nadlan' },
      { label: '📦 יד שנייה',          value: 'sale2' },
      { label: '🐾 חיות מחמד',         value: 'pet' },
      { label: '📚 שיעורים פרטיים',    value: 'teacher' },
      { label: '💼 דרושים',            value: 'required' },
      { label: '🤝 שיתופי פעולה',     value: 'coworker' },
    ]);
  }

  selectPublishCategory(namespace: string, label: string, onNavigate?: () => void): void {
    this.userSay(label);
    this.typingThen(500, () => {
      this.botSay('מעולה! פותח עבורך את טופס הפרסום ל' + label + '...');
      setTimeout(() => {
        this.router.navigate(['/publish', namespace]);
        onNavigate?.();
      }, 700);
    });
  }

  private startNavigate(): void {
    this.mode$.next('navigate');
    this.botSay('לאן תרצה לנווט?', [
      { label: '🏠 דף הבית',           value: '/' },
      { label: '🚗 רכבים',              value: '/shop/car' },
      { label: '🏘️ נדל"ן',              value: '/shop/nadlan' },
      { label: '📦 יד שנייה',           value: '/shop/sale2' },
      { label: '🐾 חיות מחמד',         value: '/shop/pet' },
      { label: '📚 שיעורים פרטיים',    value: '/shop/teacher' },
      { label: '💼 דרושים',             value: '/shop/required' },
      { label: '📢 פרסם מודעה',        value: '/publish' },
      { label: '👤 האזור האישי',        value: '/personal' },
    ]);
  }

  navigateTo(label: string, path: string, onNavigate?: () => void): void {
    this.userSay(label);
    const [route, query] = path.split('?');
    const queryParams: Record<string, string> = {};
    if (query) {
      query.split('&').forEach(p => {
        const [k, v] = p.split('=');
        queryParams[k] = v;
      });
    }
    this.typingThen(400, () => {
      this.botSay('מנווט אותך ל' + label + '...');
      setTimeout(() => {
        this.router.navigate([route], { queryParams });
        onNavigate?.();
      }, 700);
    });
  }

  private startQA(): void {
    this.mode$.next('qa');
    this.botSay('בחר שאלה:', [
      { label: '💰 מה עולה פרסום?',         value: 'pricing' },
      { label: '📋 איך מפרסמים מודעה?',     value: 'how' },
      { label: '⏱️ כמה זמן המודעה פעילה?',  value: 'duration' },
      { label: '🏅 מה זה VIP?',              value: 'vip' },
      { label: '📞 יצירת קשר',              value: 'contact' },
    ]);
  }

  answerQA(value: string, label: string): void {
    this.userSay(label);
    // "כמה עולה פרסום?" — first ask which payment type, then show that
    // type's cards (showPlanCards). Prices and benefits come from the API
    // inside the cards component; nothing about them is hardcoded here.
    if (value === 'pricing') {
      this.typingThen(400, () => {
        this.botSay('יש שתי דרכים לפרסם. מה מעניין אותך?', [
          { label: 'תשלום לפי מודעה', value: '_price_ad' },
          { label: 'מנוי לעסקים (חודשי / שנתי)', value: '_price_sub' },
        ]);
      });
      return;
    }
    // "מה זה VIP?" — VIP = the business subscription, so the answer is the
    // detailed Go / Plus / Pro cards (allowance + every benefit + prices from
    // the API), not a hardcoded text.
    if (value === 'vip') {
      this.showPlanCards(
        'subscription_plans',
        'VIP הוא מנוי לעסקים ולמפרסמים קבועים: מכסת מודעות פעילות במקביל, קידום בחיפוש והטבות לפי המסלול.\nאלה המסלולים וכל מה שכלול בכל אחד מהם:',
      );
      return;
    }
    // "יצירת קשר" — every channel from company-api/profile/info/ (the data
    // of the /contact page): phone, WhatsApp, email, address, hours, social.
    if (value === 'contact') {
      this.typingThen(400, () => {
        this.botSay('אלה כל הדרכים ליצור איתנו קשר:', undefined, 'contact');
        setTimeout(() => {
          this.botSay('אפשר גם לשוחח עם נציג כאן בצ\'אט:', [
            { label: '💬 שוחח עם נציג חי', value: '_live_chat', disabled: !this.isLoggedIn() },
            { label: '❓ שאלה נוספת',  value: '_qa_again' },
            { label: '🏠 חזור לתפריט', value: '_menu' },
          ]);
        }, 600);
      });
      return;
    }
    const answers: Record<string, string> = {
      how:      '📋 פשוט מאוד:\n1. לחץ "פרסם מודעה"\n2. בחר קטגוריה\n3. מלא את הפרטים\n4. בחר תוכנית ושלם ✅',
      duration: '⏱️ תקופת פעילות:\n• מודעה חינמית — 29 יום\n• מודגשת — 28 יום\n• SUPER GOLD — 60 יום',
    };
    this.typingThen(400, () => {
      this.botSay(answers[value] ?? 'אין לי תשובה כרגע. פנה לתמיכה.');
      const followUps: ChatOpt[] = [
        { label: '❓ שאלה נוספת',  value: '_qa_again' },
        { label: '🏠 חזור לתפריט', value: '_menu' },
      ];
      setTimeout(() => {
        this.botSay('יש שאלה נוספת?', followUps);
      }, 600);
    });
  }

  handleFollowUp(value: string): void {
    if (value === '_menu') {
      this.userSay('🏠 חזור לתפריט');
      this.typingThen(400, () => this.init());
    } else if (value === '_qa_again') {
      this.userSay('❓ שאלה נוספת');
      this.typingThen(300, () => this.startQA());
    } else if (value === '_live_chat') {
      this.userSay('💬 שוחח עם נציג חי');
      this.requireLoginThenChat();
    } else if (value === '_price_ad' || value === '_price_sub') {
      this.userSay(value === '_price_ad' ? 'תשלום לפי מודעה' : 'מנוי לעסקים');
      this.showPlanCards(value === '_price_ad' ? 'ad_plans' : 'subscription_plans');
    } else if (value === '_goto_login') {
      this.userSay('🔑 התחבר עכשיו');
      this.router.navigate(['/sign-in']);
    }
  }

  private requireLoginThenChat(): void {
    if (this.isLoggedIn()) {
      this.checkRoomsThenOpen();
    } else {
      this.typingThen(350, () => {
        this.botSay(
          '🔐 כדי לשוחח עם נציג עליך להתחבר תחילה.\nלחץ כאן להתחברות:',
          [
            { label: '🔑 התחבר עכשיו', value: '_goto_login' },
            { label: '🏠 חזור לתפריט', value: '_menu' },
          ]
        );
      });
    }
  }

  /** SCRUM: if the user has more than one room, show a picker instead of
   * silently landing in whichever room ChatConsumer picks first — same
   * behavior as market_app's live-chat entry point. Fails open (goes
   * straight to live chat) on a list-fetch error rather than blocking the
   * whole feature over it. */
  private checkRoomsThenOpen(): void {
    this.http.get<ChatRoomSummary[]>(`${SystemApp.base_url_api}/api/chat/my-rooms/`).subscribe({
      next: rooms => {
        if (rooms.length > 1) {
          this.roomsForPicker$.next(rooms);
          this.mode$.next('room_picker');
        } else {
          // No specific room picked this time — a stale id from a PREVIOUS
          // pick (earlier session in this same tab) must not leak in here.
          this.selectedRoomId$.next(null);
          this.openLiveChat();
        }
      },
      error: () => this.openLiveChat(),
    });
  }

  /** The answer of "כמה עולה פרסום?" after the payment type was chosen. */
  private showPlanCards(widget: ChatWidget, intro?: string): void {
    const isAd = widget === 'ad_plans';
    this.typingThen(400, () => {
      this.botSay(
        intro ?? (isAd
          ? 'תשלום חד-פעמי לכל מודעה. את החבילה בוחרים בסוף טופס הפרסום:'
          : 'מנוי לעסקים מאפשר כמה מודעות פעילות במקביל. אפשר להחליף בין חיוב חודשי לשנתי:'),
        undefined,
        widget,
      );
      setTimeout(() => {
        this.botSay('יש שאלה נוספת?', [
          isAd
            ? { label: 'הצג מנויים לעסקים', value: '_price_sub' }
            : { label: 'הצג תשלום לפי מודעה', value: '_price_ad' },
          { label: '❓ שאלה נוספת',  value: '_qa_again' },
          { label: '🏠 חזור לתפריט', value: '_menu' },
        ]);
      }, 600);
    });
  }

  /**
   * A plan was picked in the plan cards: hand the id of the selected billing
   * period to the VIP subscription page (/personal/vip-subscription?plan_id=),
   * which preselects that period and card. That page is behind AuthGuard, so a
   * guest is asked to log in first instead of bouncing off the guard.
   */
  selectPlan(plan: ChatPickedPlan, onNavigate?: () => void): void {
    // Per-ad plans are chosen at the END of the publish form (plan picker
    // after the ad details), so the bot continues into the publish flow.
    // SCRUM-313: a coupon is used at the same place - the plan picker at the
    // end of the form, which shows it first and pre-selected. Nothing is
    // consumed here; the publish there sends coupon_redemption_id +
    // coupon_request_id.
    if (plan.kind === 'coupon') {
      this.couponService.setPreferred(plan.id);
      this.userSay(`בחרתי לפרסם באמצעות הקופון ${plan.name}`);
      this.typingThen(400, () =>
        this.startPublish(`מעולה! בסוף טופס הפרסום הקופון ${plan.name} יופיע ראשון ומסומן - שם לוחצים "פרסם באמצעות הקופון".\nבאיזו קטגוריה תרצה לפרסם?`));
      return;
    }
    if (plan.kind === 'ad') {
      this.userSay(`בחרתי בחבילה ${plan.name}`);
      this.typingThen(400, () =>
        this.startPublish(`מעולה! את החבילה ${plan.name} בוחרים בסוף טופס הפרסום.\nבאיזו קטגוריה תרצה לפרסם?`));
      return;
    }
    const period = plan.billingPeriod === 'YEARLY' ? 'שנתי' : 'חודשי';
    this.userSay(`בחרתי במסלול ${plan.name} ${period}`);
    if (!this.isLoggedIn()) {
      this.typingThen(350, () => {
        this.botSay('🔐 כדי להצטרף למסלול עליך להתחבר תחילה.', [
          { label: '🔑 התחבר עכשיו', value: '_goto_login' },
          { label: '🏠 חזור לתפריט', value: '_menu' },
        ]);
      });
      return;
    }
    this.typingThen(400, () => {
      this.botSay('מעולה! מעביר אותך להשלמת ההצטרפות למסלול ' + plan.name + '...');
      setTimeout(() => {
        this.router.navigate(['/personal/vip-subscription'], { queryParams: { plan_id: plan.id } });
        onNavigate?.();
      }, 700);
    });
  }

  selectRoom(room: ChatRoomSummary): void {
    this.selectedRoomId$.next(room.id);
    this.openLiveChat();
  }

  deleteRoom(room: ChatRoomSummary): void {
    this.http.post<{ status: string }>(
      `${SystemApp.base_url_api}/api/chat/rooms/${room.id}/delete-for-me/`, {},
    ).subscribe({
      next: () => {
        this.roomsForPicker$.next(this.roomsForPicker$.value.filter(r => r.id !== room.id));
      },
    });
  }

  openLiveChat(): void {
    this.typingThen(350, () => {
      this.botSay('מחבר אותך לצ\'אט עם נציג... 💬');
      setTimeout(() => this.mode$.next('live_chat'), 700);
    });
  }

  backToMenu(): void {
    this.typingThen(300, () => this.init());
  }

  private userSay(text: string): void {
    this.addMsg({ id: this.uid(), role: 'user', text });
  }

  private botSay(text: string, options?: ChatOpt[], widget?: ChatWidget): void {
    this.addMsg({ id: this.uid(), role: 'bot', text, options, widget });
  }

  private addMsg(msg: ChatMsg): void {
    this.messages$.next([...this.messages$.value, msg]);
  }

  private typingThen(ms: number, fn: () => void): void {
    this.isTyping$.next(true);
    setTimeout(() => {
      this.isTyping$.next(false);
      fn();
    }, ms);
  }

  private uid(): string {
    return Date.now().toString(36) + Math.random().toString(36).slice(2);
  }
}
